import { useCallback, useEffect, useRef } from 'react';
import { ArrowUpDown, Check, ChevronDown, TriangleAlert } from 'lucide-react';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { pushEscapeLayer } from '../ui/escape-stack';
import { useClickOutside } from '../flow/useClickOutside';
import { SORT_KEYS, SORT_KEY_ORDER, type SortChoice } from './sort-people';

/**
 * The ⌘I list's "Sort" button and its menu: one row per key, then which way
 * round. Non-modal: a press outside or Esc closes it before the palette's own Esc.
 */
export function SortMenu({
  value,
  onChange,
  open,
  onOpenChange,
  loading,
  failed,
  onRetry,
}: {
  value: SortChoice;
  onChange: (choice: SortChoice) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The values for the chosen key are still loading, so the list is still A–Z. */
  loading: boolean;
  /** The values could not be loaded, so the list stays A–Z until a retry works. */
  failed: boolean;
  onRetry: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const panelRef = useClickOutside(open, close, anchorRef);
  useEffect(() => (open ? pushEscapeLayer(close) : undefined), [open, close]);
  const current = SORT_KEYS[value.key];

  // Opening moves focus into the menu (on the chosen key), so the arrows and Enter act on it.
  useEffect(() => {
    if (!open) return;
    const items = panelRef.current?.querySelectorAll<HTMLElement>('button');
    const chosen = panelRef.current?.querySelector<HTMLElement>('[role="menuitemradio"][aria-checked="true"]');
    (chosen ?? items?.[0])?.focus();
  }, [open, panelRef]);

  const moveFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button'));
    if (items.length === 0) return;
    event.preventDefault();
    const at = items.findIndex((item) => item === document.activeElement);
    let next = 0;
    if (event.key === 'ArrowDown') next = (at + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (at - 1 + items.length) % items.length;
    else if (event.key === 'End') next = items.length - 1;
    items[next].focus();
  };

  return (
    <div className="relative ml-auto shrink-0">
      <button
        ref={anchorRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-keyshortcuts="Alt+S"
        aria-label={`Sort by ${current.label}`}
        onClick={() => onOpenChange(!open)}
        className={`flex h-7 items-center gap-1.5 rounded-md border border-border-primary pl-2 pr-1.5 text-xs font-medium text-content-secondary transition-colors hover:bg-bg-secondary ${open ? 'bg-bg-secondary' : 'bg-bg-primary'}`}
      >
        <ArrowUpDown className="h-3.5 w-3.5" aria-hidden="true" />
        Sort
        <span className="font-semibold text-content-primary">{current.label}</span>
        {loading && (
          <span role="img" aria-label="Loading" className="flex text-content-tertiary">
            <LoadingSpinner size="sm" />
          </span>
        )}
        {failed && <TriangleAlert className="h-3 w-3 text-icon-warning" role="img" aria-label="Couldn't load the sort values" />}
        <ChevronDown className="h-3 w-3 text-content-tertiary" aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          aria-label="Sort people by"
          onKeyDown={moveFocus}
          className="absolute right-0 top-[calc(100%+4px)] z-20 flex w-72 flex-col rounded-[10px] border border-border-primary bg-bg-primary p-1.5 shadow-lg"
        >
          {SORT_KEY_ORDER.map((key) => {
            const on = key === value.key;
            return (
              <button
                key={key}
                type="button"
                role="menuitemradio"
                aria-checked={on}
                onClick={() => {
                  onChange({ key, reverse: false });
                  close();
                }}
                className={`grid grid-cols-[16px_minmax(0,1fr)] items-center gap-x-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-bg-secondary ${on ? 'bg-bg-secondary' : ''}`}
              >
                <span className="flex text-content-selected">{on && <Check className="h-3.5 w-3.5" aria-hidden="true" />}</span>
                <span className="flex min-w-0 flex-col gap-px">
                  <span className="text-[13px] font-semibold text-content-primary">{SORT_KEYS[key].label}</span>
                  <span className="text-[11.5px] text-content-tertiary">{SORT_KEYS[key].hint}</span>
                </span>
              </button>
            );
          })}
          {failed && (
            <div className="mx-0.5 mt-1.5 flex items-center justify-between gap-2 rounded-md bg-bg-warning px-2 py-1.5 text-[11.5px] text-content-warning">
              <span>Couldn&apos;t load the values, so the list is A–Z.</span>
              <button type="button" onClick={onRetry} className="shrink-0 font-semibold underline-offset-2 hover:underline">
                Try again
              </button>
            </div>
          )}
          <div className="mx-0.5 my-1.5 h-px bg-border-secondary" />
          <div role="group" aria-label="Order" className="grid grid-cols-2 gap-0.5 rounded-lg bg-bg-secondary p-0.5">
            {current.dirs.map((label, i) => {
              const pressed = (i === 1) === value.reverse;
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => onChange({ key: value.key, reverse: i === 1 })}
                  className={`flex h-7 items-center justify-center whitespace-nowrap rounded-md border text-xs font-semibold transition-colors ${
                    pressed ? 'border-border-primary bg-bg-primary text-content-primary' : 'border-transparent text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
